import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  BookOpen, Compass, Calendar, PlusCircle, Trash2, Edit3, ArrowLeft,
  Loader2, Save, X, Sparkles, Users, Scale, Home as HomeIcon, HeartHandshake,
  AlertCircle, ChevronRight, Printer,
} from "lucide-react";

/* SCAN AP Dashboard — Jurnal + Cronologie
   Requires authenticated user (owner or admin). */

const EMOTIONS = [
  { v: "bine", label: "Bine", color: "#3BA76D", dot: "🟢" },
  { v: "tensiune", label: "Tensiune", color: "#D4A63D", dot: "🟡" },
  { v: "tristete", label: "Tristețe", color: "#E89BB8", dot: "🟠" },
  { v: "anxietate", label: "Anxietate", color: "#C83A3A", dot: "🔴" },
  { v: "neutru", label: "Neutru", color: "#A0AABF", dot: "⚪" },
];
const emotionMeta = (v) => EMOTIONS.find((e) => e.v === v) || EMOTIONS[4];

const CATEGORIES = [
  { v: "contact", label: "Contact", icon: HeartHandshake, color: "#D4A63D" },
  { v: "comunicare", label: "Comunicare", icon: Users, color: "#5FA0E8" },
  { v: "incident", label: "Incident", icon: AlertCircle, color: "#C83A3A" },
  { v: "terapie", label: "Terapie", icon: Sparkles, color: "#E89BB8" },
  { v: "justitie", label: "Justiție", icon: Scale, color: "#9C8FE0" },
  { v: "familie", label: "Familie", icon: HomeIcon, color: "#3BA76D" },
  { v: "altul", label: "Altul", icon: BookOpen, color: "#6C7A9C" },
];
const categoryMeta = (v) => CATEGORIES.find((c) => c.v === v) || CATEGORIES[6];

const today = () => new Date().toISOString().slice(0, 10);

/* ============ JURNAL ============ */
function JournalTab({ assessmentId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: today(), emotion: "neutru", content: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scanap/${assessmentId}/journal`);
      setEntries(r.data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [assessmentId]);

  const save = async () => {
    if (!form.content.trim()) { toast.error("Scrie o notă înainte de salvare."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/scanap/journal/${editingId}`, form);
        toast.success("Notă actualizată");
      } else {
        await api.post(`/scanap/journal`, { assessment_id: assessmentId, ...form });
        toast.success("Notă salvată");
      }
      setForm({ date: today(), emotion: "neutru", content: "" });
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => {
    setEditingId(e.entry_id);
    setForm({ date: e.date, emotion: e.emotion, content: e.content });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!confirm("Ștergi această notă definitiv?")) return;
    try {
      await api.delete(`/scanap/journal/${id}`);
      toast.success("Notă ștearsă");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="dashboard-journal-tab">
      {/* Editor card */}
      <div className="p-6 rounded-md border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] tracking-widest uppercase text-[#A0AABF] mb-3">
          {editingId ? "Modifică nota" : "Adaugă o notă în jurnal"}
        </div>
        <div className="grid md:grid-cols-[160px_1fr] gap-4 items-start">
          <div className="space-y-3">
            <input
              type="date"
              data-testid="journal-date-input"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
            />
            <div className="text-[10px] tracking-widest text-[#A0AABF] uppercase">Emoție</div>
            <div className="grid grid-cols-1 gap-1.5">
              {EMOTIONS.map((e) => (
                <button
                  key={e.v}
                  data-testid={`journal-emotion-${e.v}`}
                  onClick={() => setForm({ ...form, emotion: e.v })}
                  className={`px-2.5 py-1.5 rounded-sm text-xs font-semibold border transition text-left flex items-center gap-2 ${
                    form.emotion === e.v ? "border-white text-white" : "border-white/10 text-[#A0AABF] hover:border-white/30"
                  }`}
                  style={form.emotion === e.v ? { borderColor: e.color, background: `${e.color}10` } : {}}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: e.color }} /> {e.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <textarea
              data-testid="journal-content-input"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value.slice(0, 4000) })}
              placeholder="Ce s-a întâmplat astăzi? Ce ai observat, ce ai simțit, ce te-a marcat..."
              rows={8}
              className="w-full px-4 py-3 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm leading-relaxed resize-none"
            />
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[11px] text-[#6C7A9C]">{form.content.length}/4000</span>
              <div className="flex items-center gap-2">
                {editingId && (
                  <button
                    data-testid="journal-cancel-edit"
                    onClick={() => { setEditingId(null); setForm({ date: today(), emotion: "neutru", content: "" }); }}
                    className="px-4 py-2 rounded-sm text-xs font-semibold tracking-wider border border-white/10 text-[#A0AABF] hover:text-white"
                  >
                    ANULEAZĂ
                  </button>
                )}
                <button
                  data-testid="journal-save-btn"
                  disabled={saving}
                  onClick={save}
                  className="btn-gold px-5 py-2 rounded-sm text-xs font-bold tracking-wider inline-flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingId ? "ACTUALIZEAZĂ" : "SALVEAZĂ"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Entries */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-[#A0AABF]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Se încarcă notele...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-[#6C7A9C] text-sm">Niciun jurnal încă. Începe cu prima ta notă mai sus.</div>
        ) : (
          entries.map((e) => {
            const em = emotionMeta(e.emotion);
            return (
              <div
                key={e.entry_id}
                data-testid={`journal-entry-${e.entry_id}`}
                className="p-5 rounded-md border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition"
              >
                <div className="flex items-start gap-3">
                  <div className="w-1 self-stretch rounded-full" style={{ background: em.color }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white text-sm font-semibold">{new Date(e.date).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}</span>
                      <span
                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ background: `${em.color}20`, color: em.color, border: `1px solid ${em.color}40` }}
                      >
                        {em.label}
                      </span>
                    </div>
                    <p className="text-[#F8F8F4]/90 text-sm leading-relaxed whitespace-pre-line">{e.content}</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button data-testid={`journal-edit-${e.entry_id}`} onClick={() => startEdit(e)} className="p-2 rounded-sm text-[#A0AABF] hover:text-[#D4A63D] hover:bg-white/5"><Edit3 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`journal-delete-${e.entry_id}`} onClick={() => remove(e.entry_id)} className="p-2 rounded-sm text-[#A0AABF] hover:text-[#C83A3A] hover:bg-white/5"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ============ CRONOLOGIE ============ */
function TimelineTab({ assessmentId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: today(), title: "", description: "", category: "altul" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("toate");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scanap/${assessmentId}/timeline`);
      setEvents(r.data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [assessmentId]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titlul este obligatoriu."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/scanap/timeline/${editingId}`, form);
        toast.success("Eveniment actualizat");
      } else {
        await api.post(`/scanap/timeline`, { assessment_id: assessmentId, ...form });
        toast.success("Eveniment adăugat");
      }
      setForm({ date: today(), title: "", description: "", category: "altul" });
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.event_id);
    setForm({ date: ev.date, title: ev.title, description: ev.description || "", category: ev.category });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!confirm("Ștergi acest eveniment definitiv?")) return;
    try {
      await api.delete(`/scanap/timeline/${id}`);
      toast.success("Eveniment șters");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = useMemo(() => filter === "toate" ? events : events.filter((e) => e.category === filter), [events, filter]);

  return (
    <div data-testid="dashboard-timeline-tab">
      {/* Editor card */}
      <div className="p-6 rounded-md border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] tracking-widest uppercase text-[#A0AABF] mb-3">
          {editingId ? "Modifică evenimentul" : "Adaugă un eveniment în cronologie"}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <input
            type="date"
            data-testid="timeline-date-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="px-3 py-2.5 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
          />
          <select
            data-testid="timeline-category-input"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="px-3 py-2.5 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
          >
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
        </div>
        <input
          type="text"
          data-testid="timeline-title-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 200) })}
          placeholder="Titlu scurt (ex: „Refuz contact — vineri seara”)"
          className="mt-3 w-full px-4 py-2.5 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
        />
        <textarea
          data-testid="timeline-desc-input"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 4000) })}
          placeholder="Descriere concretă a evenimentului: ce, unde, cine, când, cum..."
          rows={4}
          className="mt-3 w-full px-4 py-3 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm resize-none"
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          {editingId && (
            <button
              data-testid="timeline-cancel-edit"
              onClick={() => { setEditingId(null); setForm({ date: today(), title: "", description: "", category: "altul" }); }}
              className="px-4 py-2 rounded-sm text-xs font-semibold tracking-wider border border-white/10 text-[#A0AABF] hover:text-white"
            >
              ANULEAZĂ
            </button>
          )}
          <button
            data-testid="timeline-save-btn"
            disabled={saving}
            onClick={save}
            className="btn-gold px-5 py-2 rounded-sm text-xs font-bold tracking-wider inline-flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            {editingId ? "ACTUALIZEAZĂ" : "ADAUGĂ"}
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("toate")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === "toate" ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]" : "border-white/10 text-[#A0AABF] hover:text-white"}`}
        >
          Toate ({events.length})
        </button>
        {CATEGORIES.map((c) => {
          const count = events.filter((e) => e.category === c.v).length;
          if (!count) return null;
          return (
            <button
              key={c.v}
              onClick={() => setFilter(c.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === c.v ? "text-white" : "text-[#A0AABF] hover:text-white"}`}
              style={filter === c.v ? { background: c.color, borderColor: c.color, color: "#0A1020" } : { borderColor: `${c.color}40` }}
            >
              {c.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Timeline vertical */}
      <div className="mt-6 relative">
        {loading ? (
          <div className="text-center py-12 text-[#A0AABF]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Se încarcă evenimentele...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#6C7A9C] text-sm">Niciun eveniment în această categorie.</div>
        ) : (
          <div className="relative pl-8">
            {/* Vertical line */}
            <div className="absolute left-3 top-2 bottom-2 w-px bg-gradient-to-b from-[#D4A63D]/40 via-white/10 to-transparent" />
            {filtered.map((ev) => {
              const cat = categoryMeta(ev.category);
              const Icon = cat.icon;
              return (
                <div key={ev.event_id} data-testid={`timeline-event-${ev.event_id}`} className="relative mb-5 group">
                  {/* Dot */}
                  <div
                    className="absolute -left-6 top-2 w-6 h-6 rounded-full grid place-items-center border-2"
                    style={{ background: "#0A1020", borderColor: cat.color }}
                  >
                    <Icon className="w-3 h-3" style={{ color: cat.color }} />
                  </div>
                  <div className="p-4 rounded-md border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[#A0AABF] text-xs">{new Date(ev.date).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}</span>
                          <span
                            className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                            style={{ background: `${cat.color}20`, color: cat.color, border: `1px solid ${cat.color}40` }}
                          >
                            {cat.label}
                          </span>
                        </div>
                        <div className="text-white font-semibold mt-1.5">{ev.title}</div>
                        {ev.description && <p className="text-[#F8F8F4]/80 text-sm leading-relaxed mt-1.5 whitespace-pre-line">{ev.description}</p>}
                      </div>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button data-testid={`timeline-edit-${ev.event_id}`} onClick={() => startEdit(ev)} className="p-1.5 rounded-sm text-[#A0AABF] hover:text-[#D4A63D] hover:bg-white/5"><Edit3 className="w-3.5 h-3.5" /></button>
                        <button data-testid={`timeline-delete-${ev.event_id}`} onClick={() => remove(ev.event_id)} className="p-1.5 rounded-sm text-[#A0AABF] hover:text-[#C83A3A] hover:bg-white/5"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ ROOT ============ */
export default function ScanAPDashboard() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth() || {};
  const nav = useNavigate();
  const [tab, setTab] = useState("jurnal");
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      nav(`/autentificare?next=/scan-ap/${id}/dashboard`);
      return;
    }
    api.get(`/scanap/${id}`).then((r) => {
      setMeta(r.data);
    }).catch((e) => {
      setError(formatErr(e));
    });
  }, [id, user, authLoading, nav]);

  if (authLoading || (!meta && !error)) {
    return (
      <div className="min-h-screen bg-[#0A1020] grid place-items-center">
        <Loader2 className="w-6 h-6 text-[#D4A63D] animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A1020] flex items-center justify-center px-6" data-testid="dashboard-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-[#C83A3A] mx-auto mb-4" />
          <h2 className="text-white font-[Playfair_Display] text-2xl mb-3">Acces restricționat</h2>
          <p className="text-[#A0AABF] text-sm mb-6">{error}</p>
          <Link to="/scan-ap" className="btn-gold px-6 py-2.5 rounded-sm text-xs font-bold tracking-wider inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Înapoi la SCAN AP
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1020] text-white" data-testid="scanap-dashboard">
      {/* Header banner */}
      <section className="pt-28 pb-8 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/scan-ap" className="inline-flex items-center gap-1.5 text-[#A0AABF] hover:text-[#D4A63D] text-xs tracking-widest uppercase mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Toate evaluările
          </Link>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A0AABF] mb-2">
                Dashboard · Jurnal & Cronologie
              </div>
              <h1 className="font-[Playfair_Display] text-3xl md:text-5xl text-white leading-tight">
                Observațiile tale despre <span className="text-[#D4A63D]">{meta?.child_pseudonym || "copil"}</span>
              </h1>
              <p className="text-[#A0AABF] text-sm mt-2 max-w-2xl">
                Documentează concret ce observi. Notele și evenimentele pot fi folosite ulterior într-o consultație cu un psiholog sau avocat.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`${api.defaults.baseURL}/scanap/${id}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="dashboard-pdf-btn"
                className="px-4 py-2.5 rounded-sm text-xs font-bold tracking-widest border border-[#D4A63D]/40 text-[#D4A63D] hover:bg-[#D4A63D]/10 inline-flex items-center gap-2"
              >
                <Printer className="w-3.5 h-3.5" /> RAPORT PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-5xl mx-auto px-6 pt-6">
        <div className="flex items-center gap-1 border-b border-white/10">
          <button
            data-testid="dashboard-tab-journal"
            onClick={() => setTab("jurnal")}
            className={`px-5 py-3 text-sm font-semibold tracking-wider inline-flex items-center gap-2 transition ${
              tab === "jurnal" ? "text-[#D4A63D] border-b-2 border-[#D4A63D]" : "text-[#A0AABF] hover:text-white"
            }`}
          >
            <BookOpen className="w-4 h-4" /> JURNAL
          </button>
          <button
            data-testid="dashboard-tab-timeline"
            onClick={() => setTab("cronologie")}
            className={`px-5 py-3 text-sm font-semibold tracking-wider inline-flex items-center gap-2 transition ${
              tab === "cronologie" ? "text-[#D4A63D] border-b-2 border-[#D4A63D]" : "text-[#A0AABF] hover:text-white"
            }`}
          >
            <Calendar className="w-4 h-4" /> CRONOLOGIE
          </button>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-8 pb-24">
        {tab === "jurnal" ? <JournalTab assessmentId={id} /> : <TimelineTab assessmentId={id} />}
      </section>
    </div>
  );
}
